import React from 'react';
import { X, Calendar, CreditCard, CheckCircle, Clock } from 'lucide-react';

const PaymentHistoryModal = ({
  isOpen,
  onClose,
  payments = [],
  isVendorView = false // If true, show service amount (amount - platform_fee) instead of full payment amount
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `$${Number(amount || 0).toFixed(2)}`;
  };

  const getPaymentTypeLabel = (type) => {
    return type === 'deposit' ? 'Deposit' : 'Full Payment';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') {
      return <CheckCircle className="w-4 h-4 text-black" />;
    }
    return <Clock className="w-4 h-4 text-gray-500" />;
  };

  const getStatusColor = (status) => {
    if (status === 'completed') {
      return 'bg-black text-white';
    }
    return 'bg-gray-200 text-black';
  };

  const completedPayments = payments.filter(p => p.status === 'completed');
  
  // For vendors: calculate total service amount (amount - platform_fee)
  // For clients: calculate total paid amount (full amount)
  const totalPaid = completedPayments.reduce((sum, p) => {
    if (isVendorView) {
      const platformFee = parseFloat(p.platform_fee || 0);
      const paymentAmount = parseFloat(p.amount || 0);
      return sum + (paymentAmount - platformFee);
    }
    return sum + Number(p.amount || 0);
  }, 0);

  const getStatusLabel = (status) => {
    const statusMap = {
      'completed': 'Completed',
      'pending': 'Pending',
      'failed': 'Failed',
      'cancelled': 'Cancelled',
      'refunded': 'Refunded'
    };
    return statusMap[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-[100] overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto relative my-4 sm:my-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 hover:text-black transition-colors z-10 p-1"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b-2 border-black">
          <h2 className="text-lg sm:text-xl font-bold text-black mb-2">Payment History</h2>
          <p className="text-xs sm:text-sm text-gray-600">
            View all payment transactions for this booking
          </p>
        </div>

        {/* Payment Summary */}
        {completedPayments.length > 0 && (
          <div className="p-4 sm:p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">
                {isVendorView ? 'Total Service Amount:' : 'Total Paid:'}
              </span>
              <span className="text-base sm:text-lg font-bold text-black">{formatAmount(totalPaid)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Total Transactions:</span>
              <span className="text-xs sm:text-sm font-medium text-black">{completedPayments.length}</span>
            </div>
          </div>
        )}

        {/* Payment List */}
        <div className="p-4 sm:p-6">
          {completedPayments.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600">No completed payments available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="bg-white border-2 border-black rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getStatusIcon(payment.status)}
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {getStatusLabel(payment.status)}
                        </span>
                      </div>
                      {payment.payment_reference && (
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-medium text-black break-all">
                            {payment.payment_reference}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="break-words">{formatDate(payment.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base sm:text-lg font-bold text-black mb-1">
                        {isVendorView ? (() => {
                          // For vendors: show service amount (amount - platform_fee)
                          const platformFee = parseFloat(payment.platform_fee || 0);
                          const paymentAmount = parseFloat(payment.amount || 0);
                          const serviceAmount = paymentAmount - platformFee;
                          return formatAmount(serviceAmount);
                        })() : formatAmount(payment.amount)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {getPaymentTypeLabel(payment.payment_type)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Details */}
                  <div className="pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-600">Method:</span>
                      <span className="ml-2 font-medium text-black capitalize">
                        {payment.payment_method || 'Card'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Processor:</span>
                      <span className="ml-2 font-medium text-black capitalize">
                        {payment.payment_processor || 'Stripe'}
                      </span>
                    </div>
                    {payment.transaction_id && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-600">Transaction ID:</span>
                        <span className="ml-2 font-medium text-black text-xs break-all">
                          {payment.transaction_id}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t-2 border-gray-300">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-xs sm:text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;


